<template>
  <div
    class="period-picker-container relative flex flex-col"
    :style="{width:containerWidth}"
  >
    <BcTab
      class="w-full"
      :type="props.tabType"
      :tab-list="tabList"
      :selected-tab="selectedTab"
      color="dark"
      @click="tabClick"
    />
    <div class="date-range-container">
      <BcDateRange
        v-if="calendarOpen"
        v-model:is-open="calendarOpen"
        :is-dark="props.isDark"
        :max-date="props.maxDate"
        :min-date="props.minDate"
        :lang="props.lang"
        :date-format="props.dateFormat"
        :range="range"
        :calendar-position="props.calendarPosition"
        @change-st-date-end-date="changeRange"
      />
    </div>
  </div>
</template>
<script setup lang="ts">
import { DateTime } from 'luxon'
import { computed, onBeforeMount, onMounted, ref } from 'vue'

import { makePixel } from '../../utils/pixel.util'
import BcTab from '../tab/BcTab.vue'
import type { ITab } from '../tab/tab.type'
import BcDateRange from './BcDateRange.vue'
import { DATE_FORMAT, DEFAULT_PERIOD_LIST, VIEW_PERIOD } from './period-picker.constant'

export type IProps = {
  defaultSelectedPeriod?: string
  stDate?: string
  endDate?: string
  minDate?: string
  maxDate?: string
  periodList?: ITab<string>[]
  tabType?: 'BASIC' | 'NAVI' | 'GROUP'
  width?: number | string
  isDark?: boolean
  lang?: 'ko' | 'en'
  dateFormat?: DATE_FORMAT
  calendarPosition?: 'top' | 'bottom'
}
const props = withDefaults(defineProps<IProps>(), {
  defaultSelectedPeriod: VIEW_PERIOD['1_ALL'],
  stDate: DateTime.now().minus({ day: 2 }).toFormat(DATE_FORMAT.DEFAULT_FORMAT),
  endDate: DateTime.now().minus({ day: 1 }).toFormat(DATE_FORMAT.DEFAULT_FORMAT),
  periodList: ()=> DEFAULT_PERIOD_LIST,
  tabType: 'GROUP',
  width: '100%',
  isDark: false,
  lang: 'en',
  dateFormat: DATE_FORMAT.DEFAULT_FORMAT,
  calendarPosition: 'bottom'
})
const emits = defineEmits([ 'getPeriod' ])

const calendarOpen = ref(false)
const isMounted = ref(false)
const selectedTab = ref(props.defaultSelectedPeriod)
const range = ref({
  selectedTab: selectedTab.value,
  start: DateTime.fromFormat(props.stDate, DATE_FORMAT.DEFAULT_FORMAT).toFormat(props.dateFormat),
  end: DateTime.fromFormat(props.endDate, DATE_FORMAT.DEFAULT_FORMAT).toFormat(props.dateFormat),
})
const containerWidth = computed(()=> {
  return makePixel(props.width)
})

const tabList = computed(() => {
  const leftIcon = selectedTab.value === 'selector' ? 'calendar-apply' : 'calendar';

  return props.periodList.map((item) => {
    if (item.value === 'selector') {
      return { ...item, leftIcon };
    }
    return item;
  });
});


const changeRange = (change: {start:string, end:string}) => {
  range.value.start = change.start
  range.value.end = change.end
  emits('getPeriod', range.value)
}

const tabClick = (param: {label: string, value: string}) => {
  selectedTab.value = param.value
  range.value.selectedTab = selectedTab.value

  if (selectedTab.value === VIEW_PERIOD.SELECTOR) {
    if (isMounted.value) {
      calendarOpen.value = true
    }
  } else {
    range.value.end = DateTime.fromFormat(props.endDate, DATE_FORMAT.DEFAULT_FORMAT).toFormat(props.dateFormat)
    const count = selectedTab.value.split('_')[0]
    const unit = selectedTab.value.split('_')[1]

    switch (unit) {
      case 'day':
        range.value.start = DateTime.now().minus({ day: Number(count) }).toFormat(props.dateFormat)
        break
      case 'week':
        range.value.start = DateTime.now().minus({ week: Number(count) }).toFormat(props.dateFormat)
        break
      case 'month':
        range.value.start = DateTime.now().minus({ month: Number(count) }).toFormat(props.dateFormat)
        break
      case 'year':
        range.value.start = DateTime.now().minus({ year: Number(count) }).toFormat(props.dateFormat)
        break
      case 'ytd':
        range.value.start = DateTime.now().minus({ year: Number(count) }).startOf('year').toFormat(props.dateFormat)
        break
      case 'all':
        range.value.start = DateTime.fromFormat('2009-01-01', 'yyyy-MM-dd').toFormat(props.dateFormat)
        break
    }
    emits('getPeriod', range.value)
  }
}

onBeforeMount(()=> {
  tabClick({
    label: selectedTab.value,
    value: selectedTab.value
  })
  // emits('getPeriod', range.value)
})

onMounted(()=> {
  isMounted.value = true
  // if (selectedTab.value === VIEW_PERIOD.SELECTOR) {
  //   calendarOpen.value = true
  // }
})
</script>


<style scoped lang="scss">
:deep(.bc-tab-wrap) {
  flex-grow: 1;
}
</style>